import { useState, useEffect, useRef, useCallback } from 'react';

// Text measurement utility for auto-fit functionality
function measureTextWidth(text, font = '14px system-ui, sans-serif') {
  if (typeof document === 'undefined') return 0;
  
  // Create a canvas for text measurement
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  context.font = font;
  
  return context.measureText(String(text)).width;
}

// Custom hook for resizable columns functionality with improved auto-fit and persistence
export const useResizableColumns = (tableId, initialColumns, userRole = 'default') => {
  const [columns, setColumns] = useState(initialColumns);
  const [columnVisibility, setColumnVisibility] = useState(
    initialColumns.reduce((acc, col) => ({ ...acc, [col.id]: !col.hidden }), {})
  );
  const [isResizing, setIsResizing] = useState(false);
  const [resizingColumn, setResizingColumn] = useState(null);
  const tableRef = useRef(null);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);
  const debounceTimeoutRef = useRef(null);
  
  // Generate storage key with role for proper isolation
  const getStorageKey = useCallback((suffix) => {
    return `colwidths:${tableId}:${userRole}:${suffix}`;
  }, [tableId, userRole]);

  // Load saved column state from localStorage with role-based keys
  const hasLoaded = useRef(false);
  
  useEffect(() => {
    if (hasLoaded.current) return;
    
    try {
      const savedColumns = localStorage.getItem(getStorageKey('columns'));
      const savedVisibility = localStorage.getItem(getStorageKey('visibility'));
      
      if (savedColumns) {
        const parsedColumns = JSON.parse(savedColumns);
        // Merge with initial columns to handle new columns
        const mergedColumns = initialColumns.map(initCol => {
          const savedCol = parsedColumns.find(sc => sc.id === initCol.id);
          return savedCol ? { ...initCol, width: savedCol.width } : initCol;
        });
        setColumns(mergedColumns);
      } else {
        // Use initial columns as-is if no saved state
        setColumns(initialColumns);
      }
      
      if (savedVisibility) {
        const parsedVisibility = JSON.parse(savedVisibility);
        setColumnVisibility(parsedVisibility);
      }
      
      hasLoaded.current = true;
    } catch (error) {
      console.warn('Failed to load column preferences:', error);
      hasLoaded.current = true;
    }
  }, [getStorageKey]);

  // Debounced save to localStorage to reduce reflows
  const debouncedSave = useCallback((newColumns, newVisibility) => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    
    debounceTimeoutRef.current = setTimeout(() => {
      try {
        const widthMap = newColumns.reduce((acc, col) => {
          acc[col.id] = col.width;
          return acc;
        }, {});
        
        localStorage.setItem(getStorageKey('columns'), JSON.stringify(newColumns));
        localStorage.setItem(getStorageKey('visibility'), JSON.stringify(newVisibility));
        localStorage.setItem(getStorageKey('widths'), JSON.stringify(widthMap));
      } catch (error) {
        console.warn('Failed to save column preferences:', error);
      }
    }, 150); // 150ms debounce
  }, [getStorageKey]);

  // Handle column resize start with improved pixel-based calculations
  const handleResizeStart = useCallback((columnId, startX) => {
    setIsResizing(true);
    setResizingColumn(columnId);
    startXRef.current = startX;
    
    const column = columns.find(col => col.id === columnId);
    const columnElement = tableRef.current?.querySelector(`[data-column-id="${columnId}"]`);
    
    if (columnElement) {
      startWidthRef.current = columnElement.offsetWidth;
    } else {
      // Fallback: parse existing width or use default
      const widthStr = column.width || '120px';
      startWidthRef.current = parseInt(widthStr.replace('px', '')) || 120;
    }
    
    const handleMouseMove = (e) => {
      if (!isResizing || resizingColumn !== columnId) return;
      
      const deltaX = e.clientX - startXRef.current;
      let newWidth = startWidthRef.current + deltaX;
      
      // Apply min/max constraints and round to avoid 1px drift
      newWidth = Math.round(Math.max(110, Math.min(560, newWidth)));
      
      setColumns(prevColumns => {
        // Only update if width actually changed to prevent unnecessary renders
        const currentColumn = prevColumns.find(col => col.id === columnId);
        const currentWidth = parseInt(currentColumn?.width?.replace('px', '')) || 120;
        
        if (Math.abs(currentWidth - newWidth) < 1) {
          return prevColumns; // No meaningful change
        }
        
        return prevColumns.map(col => 
          col.id === columnId 
            ? { ...col, width: `${newWidth}px` }
            : col
        );
      });
    };
    
    const handleMouseUp = () => {
      setIsResizing(false);
      setResizingColumn(null);
      
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      
      // Force layout recalculation after resize
      if (tableRef.current) {
        const table = tableRef.current.querySelector('table');
        if (table) {
          // Micro reflow to ensure alignment
          table.style.transform = 'translateZ(0)';
          void table.offsetWidth;
          table.style.transform = '';
          
          // Dispatch events for layout sync
          window.dispatchEvent(new Event('resize'));
          table.dispatchEvent(new Event('orders:layout-sync'));
        }
      }
      
      // Save after resize is complete
      setTimeout(() => {
        const currentColumnsRef = columns;
        debouncedSave(currentColumnsRef, columnVisibility);
      }, 0);
    };
    
    const handleTouchMove = (e) => {
      if (e.touches.length > 0) {
        const touch = e.touches[0];
        handleMouseMove({ clientX: touch.clientX });
      }
    };
    
    const handleTouchEnd = () => {
      handleMouseUp();
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [columns, isResizing, resizingColumn, debouncedSave, columnVisibility]);

  // Enhanced auto-fit functionality that measures actual content
  const measureColumnContent = useCallback((columnId) => {
    if (!tableRef.current) return 120;
    
    const column = columns.find(col => col.id === columnId);
    if (!column) return 120;
    
    // Measure header text width
    const headerWidth = measureTextWidth(column.title, 'bold 14px system-ui, sans-serif');
    
    // Find all cells for this column and measure their content
    const cells = tableRef.current.querySelectorAll(`[data-column-id="${columnId}"]`);
    let maxCellWidth = 0;
    
    cells.forEach(cell => {
      const textContent = cell.textContent || '';
      const cellWidth = measureTextWidth(textContent, '14px system-ui, sans-serif');
      maxCellWidth = Math.max(maxCellWidth, cellWidth);
    });
    
    // Calculate optimal width with padding and icon space
    const padding = 32; // Left/right padding
    const iconSpace = column.sortable ? 20 : 0; // Space for sort icons
    const optimalWidth = Math.max(headerWidth, maxCellWidth) + padding + iconSpace;
    
    // Clamp to min/max constraints and round to avoid 1px drift
    return Math.round(Math.min(560, Math.max(110, optimalWidth)));
  }, [columns]);

  // Enhanced double-click auto-fit with content measurement
  const handleAutoFit = useCallback((columnId) => {
    setColumns(prevColumns => {
      const column = prevColumns.find(col => col.id === columnId);
      if (!column) return prevColumns;
      
      // Get column constraints
      const MIN = parseInt(column.minWidth) || 80;
      const MAX = parseInt(column.maxWidth) || 520;
      
      // Measure optimal width
      const optimalWidth = measureColumnContent(columnId);
      
      // Clamp to min/max constraints
      const clampedWidth = Math.max(MIN, Math.min(optimalWidth, MAX));
      
      const newColumns = prevColumns.map(col => 
        col.id === columnId 
          ? { ...col, width: `${clampedWidth}px` }
          : col
      );
      
      // Force layout recalculation after auto-fit
      setTimeout(() => {
        if (tableRef.current) {
          const table = tableRef.current.querySelector('table');
          if (table) {
            table.style.transform = 'translateZ(0)';
            void table.offsetWidth;
            table.style.transform = '';
            window.dispatchEvent(new Event('resize'));
            table.dispatchEvent(new Event('orders:layout-sync'));
          }
        }
      }, 0);
      
      return newColumns;
    });
  }, [measureColumnContent, tableRef]);

  // Handle column visibility toggle with layout recalculation
  const toggleColumnVisibility = useCallback((columnId) => {
    setColumnVisibility(prevVisibility => {
      const newVisibility = { ...prevVisibility, [columnId]: !prevVisibility[columnId] };
      
      // Prevent hiding all columns
      const visibleColumns = Object.values(newVisibility).filter(Boolean);
      if (visibleColumns.length === 0) {
        return prevVisibility; // Don't allow hiding all columns
      }
      
      // Trigger a measured reflow after column toggle to keep header/body aligned
      requestAnimationFrame(() => {
        if (tableRef.current) {
          const table = tableRef.current.querySelector('table');
          if (table) {
            // Force layout recalculation
            table.style.transform = 'translateZ(0)';
            void table.offsetHeight;
            void table.offsetWidth;
            table.style.transform = '';
            
            // Dispatch custom reflow event
            table.dispatchEvent(new Event('columns:reflow'));
            
            // Dispatch resize for layout listeners
            if (typeof window !== 'undefined') {
              window.dispatchEvent(new Event('resize'));
            }
            
            // Dispatch layout sync event
            table.dispatchEvent(new Event('orders:layout-sync'));
          }
        }
      });
      
      return newVisibility;
    });
  }, [tableRef]);

  // Enhanced keyboard support for column resizing with proper step sizes
  const handleKeyboardResize = useCallback((columnId, direction, isShift = false) => {
    setColumns(prevColumns => {
      const column = prevColumns.find(col => col.id === columnId);
      const currentWidthStr = column?.width || '120px';
      const currentWidth = parseInt(currentWidthStr.replace('px', '')) || 120;
      
      // Use 10px steps normally, 30px steps with Shift
      const stepSize = isShift ? 30 : 10;
      let newWidth = direction === 'increase' ? currentWidth + stepSize : currentWidth - stepSize;
      
      // Apply min/max constraints (110px to 560px)
      newWidth = Math.max(110, Math.min(560, newWidth));
      
      const newColumns = prevColumns.map(col => 
        col.id === columnId 
          ? { ...col, width: `${newWidth}px` }
          : col
      );
      
      return newColumns;
    });
  }, []);

  // Get visible columns
  const visibleColumns = columns.filter(col => columnVisibility[col.id]);

  // Reset to defaults with proper cleanup
  const resetColumns = useCallback(() => {
    setColumns(initialColumns);
    setColumnVisibility(
      initialColumns.reduce((acc, col) => ({ ...acc, [col.id]: !col.hidden }), {})
    );
    
    // Clear all stored preferences for this table/role combination
    localStorage.removeItem(getStorageKey('columns'));
    localStorage.removeItem(getStorageKey('visibility'));
    localStorage.removeItem(getStorageKey('widths'));
  }, [initialColumns, getStorageKey]);
  
  // Save state changes when columns or visibility change (but not on initial load)
  useEffect(() => {
    if (hasLoaded.current) {
      debouncedSave(columns, columnVisibility);
    }
  }, [columns, columnVisibility, debouncedSave]);
  
  // Cleanup debounce timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  return {
    columns,
    visibleColumns,
    columnVisibility,
    isResizing,
    resizingColumn,
    tableRef,
    handleResizeStart,
    handleAutoFit,
    toggleColumnVisibility,
    handleKeyboardResize,
    resetColumns
  };
};